import { useEffect, useState } from "react";
import { Check, Send } from "lucide-react";

import { Button, Input, Textarea } from "@/components/ui";
import type { ContractTemplateField } from "@/types/contractIntelligence.types";
import { cn } from "@/lib/utils";

interface TemplateFieldInputProps {
  field: ContractTemplateField;
  onSubmit: (value: string | string[]) => void;
}

export function TemplateFieldInput({ field, onSubmit }: TemplateFieldInputProps) {
  const [text, setText] = useState("");
  const [picks, setPicks] = useState<string[]>([]);

  useEffect(() => {
    setText("");
    setPicks([]);
  }, [field.id]);

  if (field.kind === "select" || field.kind === "chips") {
    return (
      <div className="ml-9 mt-1 space-y-2.5">
        <div className="flex flex-wrap gap-1.5">
          {field.options?.map((option) => (
            <Button
              key={option}
              type="button"
              variant="outline"
              size="sm"
              className="h-8 rounded-full px-3 text-xs"
              onClick={() => onSubmit(option)}
            >
              {option}
            </Button>
          ))}
        </div>
        {field.kind === "select" ? (
          <div className="flex gap-2">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Or type a custom answer…"
              className="h-9 text-xs"
            />
            <Button type="button" size="sm" disabled={!text.trim()} onClick={() => onSubmit(text.trim())}>
              Send
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  if (field.kind === "chips-multi") {
    const toggle = (option: string) => {
      setPicks((prev) => (prev.includes(option) ? prev.filter((x) => x !== option) : [...prev, option]));
    };

    return (
      <div className="ml-9 mt-1 space-y-2.5">
        <div className="flex flex-wrap gap-1.5">
          {field.options?.map((option) => {
            const active = picks.includes(option);
            return (
              <Button
                key={option}
                type="button"
                size="sm"
                variant={active ? "default" : "outline"}
                className="h-8 rounded-full px-3 text-xs"
                onClick={() => toggle(option)}
              >
                {active ? <Check className="size-3" aria-hidden="true" /> : null}
                {option}
              </Button>
            );
          })}
        </div>
        <Button type="button" size="sm" disabled={picks.length === 0} onClick={() => onSubmit(picks)}>
          Continue ({picks.length})
        </Button>
      </div>
    );
  }

  if (field.kind === "money" || field.kind === "percent" || field.kind === "text") {
    const prefix = field.kind === "money" ? "$" : null;
    const suffix = field.kind === "percent" ? "%" : null;

    return (
      <div className="ml-9 mt-1 flex gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
          {prefix ? <span className="font-mono text-xs text-muted-foreground">{prefix}</span> : null}
          <Input
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && text.trim()) onSubmit(text.trim());
            }}
            placeholder={field.placeholder}
            className={cn("h-auto border-0 bg-transparent p-0 shadow-none focus-visible:ring-0", field.kind !== "text" && "font-mono")}
          />
          {suffix ? <span className="font-mono text-xs text-muted-foreground">{suffix}</span> : null}
        </div>
        <Button
          type="button"
          size="icon"
          className="shrink-0"
          disabled={!text.trim()}
          onClick={() => onSubmit(text.trim())}
          aria-label="Submit answer"
        >
          <Send className="size-4" />
        </Button>
      </div>
    );
  }

  if (field.kind === "longtext") {
    return (
      <div className="ml-9 mt-1 space-y-2">
        <Textarea
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder={field.placeholder}
          className="text-xs"
        />
        <Button type="button" size="sm" onClick={() => onSubmit(text.trim() || "—")}>
          {text.trim() ? "Continue" : "Skip"}
        </Button>
      </div>
    );
  }

  return null;
}
